(() => {
  if (window.__awMainVisualLoaded) {
    return;
  }

  window.__awMainVisualLoaded = true;

  const instances = new Map();
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const selectors = {
    root: "[data-aw-main-visual]",
    slide: "[data-aw-main-visual-slide]",
    progress: "[data-aw-main-visual-progress]",
    prev: "[data-aw-main-visual-prev]",
    next: "[data-aw-main-visual-next]",
  };

  class AWMainVisual {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.slides = Array.from(root.querySelectorAll(selectors.slide));
      this.progress = root.querySelector(".aw-main-visual__progress");
      this.progressBar = root.querySelector(selectors.progress);
      this.prevButton = root.querySelector(selectors.prev);
      this.nextButton = root.querySelector(selectors.next);
      this.autoplaySeconds = Number.parseInt(root.dataset.autoplaySeconds || "5", 10);
      this.duration = Number.isFinite(this.autoplaySeconds) && this.autoplaySeconds > 0 ? this.autoplaySeconds * 1000 : 5000;
      this.timeoutId = null;
      this.isEditorPaused = false;
      this.activeIndex = this.getInitialIndex();
      this.onReducedMotionChange = this.handleReducedMotionChange.bind(this);
      this.onClick = this.handleClick.bind(this);

      this.root.style.setProperty("--aw-main-visual-duration", `${this.duration}ms`);
      this.syncProgressVisibility();
      this.showSlide(this.activeIndex, { restartProgress: false });
      this.root.addEventListener("click", this.onClick);

      if (typeof reducedMotionQuery.addEventListener === "function") {
        reducedMotionQuery.addEventListener("change", this.onReducedMotionChange);
      } else if (typeof reducedMotionQuery.addListener === "function") {
        reducedMotionQuery.addListener(this.onReducedMotionChange);
      }

      if (this.canAutoplay()) {
        this.start();
      } else {
        this.stop();
      }
    }

    getInitialIndex() {
      const initialIndex = this.slides.findIndex((slide) => {
        return slide.classList.contains("active") || slide.classList.contains("aw-main-visual__slide--active");
      });

      return initialIndex > -1 ? initialIndex : 0;
    }

    canAutoplay() {
      return this.slides.length > 1 && !reducedMotionQuery.matches && !this.isEditorPaused;
    }

    shouldShowProgress() {
      return this.slides.length > 1 && !reducedMotionQuery.matches;
    }

    syncProgressVisibility() {
      if (!this.progress) {
        return;
      }

      const hidden = !this.shouldShowProgress();
      this.progress.hidden = hidden;
      this.progress.classList.toggle("is-hidden", hidden);

      if (hidden) {
        this.stopProgress();
      }
    }

    handleClick(event) {
      const prevButton = event.target.closest(selectors.prev);
      const nextButton = event.target.closest(selectors.next);

      if (prevButton && this.root.contains(prevButton)) {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
        return;
      }

      if (nextButton && this.root.contains(nextButton)) {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      }
    }

    clearTimer() {
      if (this.timeoutId) {
        window.clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }

    stopProgress() {
      if (!this.progressBar) {
        return;
      }

      this.progressBar.classList.remove("is-animating");
      this.progressBar.style.animation = "none";
      this.progressBar.getBoundingClientRect();
      this.progressBar.style.animation = "";
    }

    restartProgress() {
      if (!this.progressBar) {
        return;
      }

      this.syncProgressVisibility();

      if (!this.canAutoplay()) {
        this.stopProgress();
        return;
      }

      this.progressBar.classList.remove("is-animating");
      this.progressBar.style.animation = "none";
      this.progressBar.getBoundingClientRect();
      this.progressBar.style.animation = "";
      this.progressBar.classList.add("is-animating");
    }

    showSlide(index, options = {}) {
      if (!this.slides.length) {
        return;
      }

      const restartProgress = options.restartProgress !== false;
      const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;

        slide.classList.toggle("active", isActive);
        slide.classList.toggle("aw-main-visual__slide--active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");

        if (slide.tagName === "A") {
          if (isActive) {
            slide.removeAttribute("tabindex");
          } else {
            slide.setAttribute("tabindex", "-1");
          }
        }
      });

      this.activeIndex = nextIndex;

      if (restartProgress) {
        this.restartProgress();
      } else {
        this.syncProgressVisibility();
      }
    }

    goTo(index) {
      if (this.slides.length <= 1) {
        return;
      }

      this.clearTimer();
      this.showSlide(index);

      if (this.canAutoplay()) {
        this.queueNext();
      }
    }

    queueNext() {
      this.clearTimer();

      if (!this.canAutoplay()) {
        return;
      }

      this.timeoutId = window.setTimeout(() => {
        this.showSlide(this.activeIndex + 1);
        this.queueNext();
      }, this.duration);
    }

    start() {
      if (!this.canAutoplay()) {
        this.stop();
        return;
      }

      this.root.style.setProperty("--aw-main-visual-duration", `${this.duration}ms`);
      this.restartProgress();
      this.queueNext();
    }

    stop() {
      this.clearTimer();
      this.syncProgressVisibility();
      this.stopProgress();
    }

    selectBlock(blockId) {
      const slideIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);

      if (slideIndex === -1) {
        return;
      }

      this.isEditorPaused = true;
      this.stop();
      this.showSlide(slideIndex, { restartProgress: false });
    }

    deselectBlock() {
      this.isEditorPaused = false;

      if (this.canAutoplay()) {
        this.start();
      } else {
        this.syncProgressVisibility();
      }
    }

    handleReducedMotionChange() {
      if (this.canAutoplay()) {
        this.start();
      } else {
        this.stop();
        this.showSlide(this.activeIndex, { restartProgress: false });
      }
    }

    destroy() {
      this.stop();
      this.root.removeEventListener("click", this.onClick);

      if (typeof reducedMotionQuery.removeEventListener === "function") {
        reducedMotionQuery.removeEventListener("change", this.onReducedMotionChange);
      } else if (typeof reducedMotionQuery.removeListener === "function") {
        reducedMotionQuery.removeListener(this.onReducedMotionChange);
      }
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const nestedRoot = target.querySelector(selectors.root);

      if (nestedRoot) {
        return nestedRoot;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWMainVisual(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance || !blockId) {
      return;
    }

    instance.selectBlock(blockId);
  });

  document.addEventListener("shopify:block:deselect", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.deselectBlock();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
